/**
 * Тексты колокольчика, панели уведомлений и пометки постановщику
 * (Блок 2 · Ф7 · В11-a; Р-132).
 *
 * ⚠️ ТЕКСТЫ БОТА СЮДА НЕ СКОПИРОВАНЫ И НЕ ПЕРЕИСПОЛЬЗУЮТСЯ. У мессенджера и у
 * сайта разные каналы и разные тексты; общее у них одно — отображаемое
 * содержание строки очереди (`display`), и только оно.
 *
 * ⚠️ ЗАГОЛОВКИ НЕЙТРАЛЬНЫ К АКТОРУ намеренно — как и у бота (третья поправка к
 * Р-66): строку «в работе» руководитель получает и тогда, когда работу
 * запустил кто-то третий, и «исполнитель взялся» было бы там ложью.
 */
import type { NotificationFeedItem, NotificationFeedKind } from '@aris/shared';

/**
 * Заголовок строки по виду события.
 *
 * ⚠️ `satisfies Record<NotificationFeedKind, …>` — несущее: вид, добавленный в
 * ленту, не соберётся без заголовка. «Один шаблон на все виды» здесь
 * невозможен по построению.
 */
const HEADLINE = {
  WORK_ASSIGNED: () => 'Вам назначена работа',
  TASK_ASSIGNED: () => 'Вам назначена задача',
  WORK_UNASSIGNED: () => 'Работа больше не за вами',
  WORK_ARCHIVED: () => 'Работа снята',
  WORK_DUE_CHANGED: () => 'Срок работы изменён',
  WORK_IN_PROGRESS: () => 'Работа начата',
  WORK_ON_REVIEW: () => 'Работа сдана на проверку',
  WORK_CLOSED: () => 'Работа закрыта',
  WORK_RETURNED: () => 'Работа возвращена на доработку',
  WORK_REOPENED: () => 'Закрытая работа открыта заново',
  // Вид один, точек две (Р-140) — заголовок выбирает точка, как у бота.
  WORK_DUE_SOON: (item) =>
    item.point === 'due_today' ? 'Сегодня срок работы' : 'Срок работы приближается',
} as const satisfies Record<NotificationFeedKind, (item: NotificationFeedItem) => string>;

export function notificationHeadline(item: NotificationFeedItem): string {
  return HEADLINE[item.kind](item);
}

export const notificationsRu = {
  bell: 'Уведомления',
  /** `{N}` — число новых, уже с «+», если их больше страницы. */
  bellWithNew: 'Уведомления, новых: {N}',
  /** Добавка к имени колокольчика, когда последнее обновление не удалось. */
  bellStale: 'не удалось обновить',
  /** Подсказка неактивного колокольчика, когда он сам сломался. */
  bellBroken: 'Уведомления недоступны, обновите страницу',
  title: 'Уведомления',
  caption:
    'События по вашим работам за 30 дней. Отметка «новое» и скрытие прочитанных хранятся в этом браузере — на другом устройстве они свои.',
  /** Разовое действие: убирает из списка всё, что сейчас без отметки «новое». */
  hideRead: 'Скрыть прочитанные',
  /** Подпись в конце списка, пока среди загруженных строк есть скрытые. */
  hiddenNote: 'Прочитанные уведомления скрыты.',
  /**
   * То же, когда в списке не осталось ни одной строки. ⚠️ Только при
   * исчерпанной ленте и удавшемся обновлении: пока есть «Показать ещё» или
   * данные устарели, «новых нет» — утверждение без основания.
   */
  hiddenEmpty: 'Прочитанные уведомления скрыты, новых нет.',
  showHidden: 'Показать скрытые',
  /**
   * Объявления для скринридера: список изменился без перехода фокуса.
   * `{N}` — сколько загруженных строк скрыто; с числом текст не повторяется
   * от нажатия к нажатию, а одинаковый текст второй раз не объявляется.
   */
  hiddenAnnounce: 'Прочитанные уведомления скрыты, всего скрыто: {N}',
  shownAnnounce: 'Скрытые уведомления показаны',
  /**
   * Вторые формулировки тех же объявлений — на случай, когда новое объявление
   * слово в слово совпало бы с предыдущим (соседняя вкладка сняла скрытие, а
   * здесь скрыли снова то же число строк).
   */
  hiddenAnnounceAgain: 'Скрыто прочитанных уведомлений: {N}',
  shownAnnounceAgain: 'Скрытые уведомления снова показаны',
  /** Показывается, когда у самого смотрящего нет привязки ни в одном мессенджере. */
  unbound: 'Бот не привязан: в мессенджер уведомления не приходят, только сюда.',
  unboundLink: 'Привязать в профиле',
  empty: 'За 30 дней уведомлений нет',
  loading: 'Уведомления загружаются',
  error: 'Не удалось загрузить уведомления',
  moreError: 'Не удалось загрузить продолжение',
  retry: 'Повторить',
  more: 'Показать ещё',
  moreLoading: 'Загружается…',
  end: 'Это все уведомления за 30 дней',
  newMark: 'новое',
  due: 'Срок',
  untitled: 'Без названия',
} as const;

/**
 * Пометка постановщику после мутации работы — потребитель `notBoundUserIds`.
 *
 * ⚠️ ФОРМА БЕЗ ГЛАГОЛА В РОДЕ. План фазы давал «<ФИО> не привязал бота» —
 * рядом с женским именем это ошибка, а пола система не хранит.
 *
 * ⚠️ «НЕ УЙДЁТ, ПОКА БОТ НЕ ПРИВЯЗАН» — А НЕ «НЕ ДОЙДЁТ» (фикс-пак 1, ревью
 * da Д-6). Сервер в момент мутации знает одно: привязки нет. Строка ждёт её 48
 * часов, и привяжет человек бота за это время — сообщение уйдёт. Категоричное
 * «не дойдёт» в этом случае ложно.
 *
 * ⚠️ ЧИСЛО — ПО ЧИСЛУ ИМЁН: одному получателю ушло одно уведомление, двоим
 * (прежний и новый исполнитель) — два.
 */
export function notBoundNoticeRu(names: readonly string[]): string {
  const list = names.join(', ');
  return names.length === 1
    ? `Уведомление не уйдёт в мессенджер, пока бот не привязан: ${list}. На сайте оно уже в колокольчике.`
    : `Уведомления не уйдут в мессенджер, пока бот не привязан: ${list}. На сайте они уже в колокольчике.`;
}

/** Имя получателя, которое форма не смогла назвать. */
export const NOT_BOUND_UNKNOWN_NAME = 'получатель';
